import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle2, Loader2 } from "lucide-react";
import { useLang, loc, CONTACTS } from "../i18n";
import { Reveal, SectionHead } from "./Reveal";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full border-b border-white/20 bg-transparent py-3.5 text-sm text-white placeholder-neutral-500 outline-none transition-colors focus:border-[#FF5A00]";

export const Contact = ({ services }) => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | done

  useEffect(() => {
    const onSelect = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("proalp:service", onSelect);
    return () => window.removeEventListener("proalp:service", onSelect);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/leads", { ...form, lang });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      toast.error(t.contact.error || formatApiError(err));
    }
  };

  const info = [
    { icon: Phone, label: t.contact.phone, value: CONTACTS.phone, href: CONTACTS.phoneHref, testid: "contact-phone" },
    { icon: MessageCircle, label: "WhatsApp", value: CONTACTS.phone, href: CONTACTS.whatsapp, testid: "contact-whatsapp" },
    { icon: Mail, label: t.contact.email, value: CONTACTS.email, href: `mailto:${CONTACTS.email}`, testid: "contact-email" },
    { icon: MapPin, label: t.contact.address, value: CONTACTS.address, href: null, testid: "contact-address" },
  ];

  return (
    <section id="contacts" data-testid="contacts-section" className="mx-auto max-w-[1500px] px-5 py-24 md:px-10 md:py-32">
      <SectionHead num={t.contact.num} title={t.contact.title} sub={t.contact.sub} testid="contacts-head" />
      <div className="grid gap-14 lg:grid-cols-2">
        <div>
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
            {info.map((item) => (
              <Reveal key={item.label} className="bg-[#0A0A0A]">
                <a
                  data-testid={item.testid}
                  href={item.href || undefined}
                  target={item.href?.startsWith("http") ? "_blank" : undefined}
                  rel="noreferrer"
                  className="group flex h-full flex-col gap-3 p-7 transition-colors duration-500 hover:bg-[#101010]"
                >
                  <item.icon size={22} className="text-[#FF5A00]" />
                  <span className="text-[11px] font-bold uppercase tracking-widest text-neutral-500">{item.label}</span>
                  <span className="text-sm font-semibold text-neutral-200 transition-colors group-hover:text-[#FF5A00]">
                    {item.value}
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="mt-5">
            <div className="overflow-hidden border border-white/10" data-testid="contact-map">
              <iframe
                title="PROALP22 map"
                src="https://www.google.com/maps?q=Lisboa,Portugal&z=11&output=embed"
                className="h-72 w-full grayscale invert-[0.9] contrast-[0.9]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="border border-white/10 bg-[#0A0A0A] p-8 md:p-10" data-testid="contact-form-wrapper">
            <h3 className="font-display text-3xl uppercase tracking-wide">{t.contact.formTitle}</h3>
            {status === "done" ? (
              <div data-testid="contact-success" className="mt-10 flex flex-col items-start gap-5 border border-[#FF5A00]/40 bg-[#FF5A00]/10 p-8">
                <CheckCircle2 size={34} className="text-[#FF5A00]" />
                <p className="text-base md:text-lg font-semibold text-white">{t.contact.success}</p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="mt-8 space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <input
                    data-testid="form-name"
                    required
                    placeholder={t.contact.name}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className={inputCls}
                  />
                  <input
                    data-testid="form-phone"
                    required
                    placeholder={t.contact.phoneF}
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className={inputCls}
                  />
                </div>
                <input
                  data-testid="form-email"
                  type="email"
                  placeholder={t.contact.emailF}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-widest text-neutral-500">
                    {t.contact.service}
                  </label>
                  <select
                    data-testid="form-service"
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    className={`${inputCls} mt-1 bg-[#0A0A0A]`}
                  >
                    <option value="">{t.contact.servicePh}</option>
                    {services.map((s) => (
                      <option key={s.id} value={loc(s.title, lang)}>
                        {loc(s.title, lang)}
                      </option>
                    ))}
                    <option value="Other">—</option>
                  </select>
                </div>
                <textarea
                  data-testid="form-message"
                  rows={4}
                  placeholder={t.contact.message}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className={`${inputCls} resize-none`}
                />
                <button
                  data-testid="form-submit"
                  type="submit"
                  disabled={status === "sending"}
                  className="group flex w-full items-center justify-center gap-3 bg-[#FF5A00] py-4 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E04F00] disabled:opacity-60"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> {t.contact.sending}
                    </>
                  ) : (
                    <>
                      {t.contact.submit}
                      <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
